import { TWidget, TViewCommonOption } from '@libs/types';
import clsx from 'clsx';
import React, { Activity, CSSProperties, ElementType } from 'react';
import { useThemeHook } from '../hooks/themeHook';
import { TCoreTextData, TTextTransformation } from '../types/data';
import { TCoreTextOptions, ALIGNMENT_ENUM } from '../types/options';
import { buildTextCssVars } from './buildTextCssVars';
import { mergeWithTheme } from '../utils/styles';
import { Highlighter } from './Highlighter';
import styles from './text.module.css';

const ALIGNMENT_CLASS: Record<string, string> = {
    [ALIGNMENT_ENUM.TOP_LEFT]: styles.alignTopLeft,
    [ALIGNMENT_ENUM.TOP_CENTER]: styles.alignTopCenter,
    [ALIGNMENT_ENUM.TOP_RIGHT]: styles.alignTopRight,
    [ALIGNMENT_ENUM.CENTER_LEFT]: styles.alignCenterLeft,
    [ALIGNMENT_ENUM.CENTER]: styles.alignCenter,
    [ALIGNMENT_ENUM.CENTER_RIGHT]: styles.alignCenterRight,
    [ALIGNMENT_ENUM.BOTTOM_LEFT]: styles.alignBottomLeft,
    [ALIGNMENT_ENUM.BOTTOM_CENTER]: styles.alignBottomCenter,
    [ALIGNMENT_ENUM.BOTTOM_RIGHT]: styles.alignBottomRight,
};

export type TCoreTextStyleProps = Pick<
    TCoreTextOptions,
    'textAlign' | 'maxLines' | 'alignment' | 'main' | 'font' | 'fontStyle' | 'padding' | 'margin' | 'textTransform' | 'disabled' | 'error'
>;

type TCoreTextViewProps = {
    /**
     * Used solely for theme resolution via useThemeHook. No behavior or interaction logic is derived from it here.
     * Optional: when omitted, a minimal widget is synthesized internally from `text`. Provide it only when the
     * widget originates server-side (so theme styles via `widget.options.styleName` are honored).
     */
    widget?: TWidget<TCoreTextData, TCoreTextOptions>;
    uid?: string;
    text?: string;
    as?: ElementType;
    className?: string;
    /** Style overrides merged on top of the resolved theme palette via mergeWithTheme. */
    styleProps?: TCoreTextStyleProps;
    /** Called when the user clicks a named action span inside the Highlighter. */
    onClickAction?: (actionName: string) => void;
} & Pick<TViewCommonOption, 'isHide' | 'isDisabled' | 'isError' | 'styleName' | 'style' | 'parentStyleNames'> &
    Pick<TCoreTextData, 'textTransformation'>;

/**
 * Composable building block for use inside complex widgets.
 * Owns only the theme hook (useThemeHook) to keep the hook count low
 * when embedded inside larger widget trees.
 *
 * Accepts pre-resolved behavior props (isHide, isDisabled, onClickAction) from
 * the parent, making it suitable for scenarios where behavior state is already
 * managed one level up.
 *
 * `widget` is optional — when omitted, a minimal widget is synthesized from `text`,
 * which is sufficient for standalone usages that don't need server-driven theme styles.
 */
const CoreTextView = ({
    widget,
    uid,
    isError,
    isHide,
    isDisabled,
    text,
    textTransformation,
    as: Tag = 'p',
    className,
    styleProps,
    style,
    styleName,
    parentStyleNames,
    onClickAction,
}: TCoreTextViewProps) => {
    const resolvedWidget = widget ?? ({ uid: uid ?? '', data: { text }, options: {} } as TWidget<TCoreTextData, TCoreTextOptions>);
    // `styleName` and `parentStyleName` come from props only — the View does not read them off `widget.options`.
    // The Widget container (CoreTextWidget) is responsible for extracting `widget.options?.styleName`
    // and passing it as an explicit `styleName` prop.
    const enrichedWidget = { ...resolvedWidget, options: { ...resolvedWidget.options, style: styleName, parentStyleNames } };
    const { palette: themePalette, colors } = useThemeHook(enrichedWidget, 'text');
    const palette: TCoreTextOptions = mergeWithTheme(themePalette, styleProps);

    const { maxLines, alignment } = palette;
    // textTransformation may be provided as a data prop (data-driven) or come from the theme style config,
    // in which case useThemeHook merges it into the palette. Prefer the explicit prop, fall back to the theme.
    // The theme-merged value can arrive as an object keyed by index ({0: {...}, 1: {...}}) rather than a real
    // array, so normalize to an array — the Highlighter guards on Array.isArray and would otherwise skip it.
    const rawTransformation = textTransformation ?? (palette as TCoreTextOptions & Pick<TCoreTextData, 'textTransformation'>).textTransformation;
    const effectiveTransformation: TTextTransformation[] | undefined = Array.isArray(rawTransformation)
        ? rawTransformation
        : rawTransformation
          ? (Object.values(rawTransformation) as TTextTransformation[])
          : undefined;

    return (
        <Activity mode={isHide ? 'hidden' : 'visible'}>
            <Tag
                data-test={resolvedWidget.uid}
                className={clsx(
                    styles.text,
                    isDisabled && styles.disabled,
                    maxLines && styles.maxLinesClamp,
                    alignment && ALIGNMENT_CLASS[alignment],
                    className,
                )}
                style={{ ...buildTextCssVars(palette, { isError, isDisabled }), ...style } as CSSProperties}
            >
                {text && <Highlighter content={text} textTransformation={effectiveTransformation} colors={colors} onClickAction={onClickAction} />}
            </Tag>
        </Activity>
    );
};

export { CoreTextView };
